<template>
  <div class="former-customers">
    <v-row>
      <v-col>
        <breadcrumb :items="breadcrumbs" />
      </v-col>
    </v-row>
    <v-card>
      <v-card-title>
        <v-container>
          <v-row>
            <v-col class="col-md-3">
              <refresh-button
                :loading="isUsersRefreshing"
                @click="refreshUsers()"
              />
            </v-col>
            <v-col class="col-md-6"></v-col>
          </v-row>
          <v-row
            justify="center"
            class="mb-0"
          >
            <v-col class="col-md-6 text-center">
              <search-input
                v-model="search"
                :filters="searchFilters"
                :active-filter="activeSearchFilter"
                @filter-change="onSearchFilterChange"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="users"
        :loading="isUsersLoading"
        disable-pagination
        hide-default-footer
      >
        <template v-slot:progress>
          <v-progress-linear
            indeterminate
            class="mt-2"
            rounded
          ></v-progress-linear>
          <p class="text-center mt-1">
            Loading...Please wait
          </p>
        </template>
        <template v-slot:body="{ items }">
          <tbody v-if="users.length > 0">
            <tr
              v-for="user in items"
              :key="user.id"
              style="cursor: pointer"
              @click="onCustomerClick(user.id)"
            >
              <td>
                <span class="primary--text font-weight-medium">{{ user.account_number }}</span>
              </td>
              <td>{{ user.name }}</td>
              <td>{{ user.phone }}</td>
              <td>
                <span class="primary--text font-weight-medium">{{ user.email }}</span>
              </td>
              <td>{{ user.deleted_at | formatDate }}</td>
              <td @click.stop>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-4
                      v-bind="attrs"
                      text
                      icon
                      @click="showRestoreUserDialog(user.id, user.name)"
                      v-on="on"
                    >
                      <v-icon>{{ icons.mdiAccountReactivate }}</v-icon>
                    </v-btn>
                  </template>
                  <span>Restore</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      ma-4
                      v-bind="attrs"
                      text
                      icon
                      @click="showPermanentDeleteDialog(user.id, user.name)"
                      v-on="on"
                    >
                      <v-icon>{{ icons.mdiDeleteForever }}</v-icon>
                    </v-btn>
                  </template>
                  <span>Permanently Delete</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-show="!isUsersLoading">
              <td
                :colspan="headers.length"
                style="text-align: center"
              >
                No former customers found
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
      <v-divider />
      <data-table-pagination
        :page="pagination.page"
        :page-count="pagination.pageCount"
        @page-change="onPageChange"
        @items-per-page-change="onItemsPerPageChange"
      />
    </v-card>

    <confirm-dialog
      :show-dialog="showConfirmRestoreDialog"
      :is-agree-button-loading="isRestoreButtonLoading"
      :title="'Restore customer?'"
      :agree-text="'Restore'"
      :message="restoreUserMessage"
      @cancel="showConfirmRestoreDialog = false"
      @agree="restoreUser()"
    />
    <confirm-dialog
      :show-dialog="showConfirmPermanentDeleteDialog"
      :is-agree-button-loading="isPermanentDeleteButtonLoading"
      :title="'Permanently delete customer?'"
      :agree-text="'Delete'"
      :message="permanentDeleteUserMessage"
      @cancel="showConfirmPermanentDeleteDialog = false"
      @agree="permanentDeleteUser()"
    />
  </div>
</template>

<script>
import axios from 'axios'
import _ from 'lodash'
import { mapGetters } from 'vuex'
import {
  mdiAccountReactivate,
  mdiDeleteForever,
} from '@mdi/js'
import hasPermission from '@/mixins/hasPermission'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import BreadCrumb from '@/components/partials/BreadCrumb.vue'
import SearchInput from '@/components/partials/SearchInput.vue'
import RefreshButton from '@/components/partials/RefreshButton.vue'
import DataTablePagination from '@/components/partials/DataTablePagination.vue'

export default {
  components: {
    ConfirmDialog,
    breadcrumb: BreadCrumb,
    SearchInput,
    RefreshButton,
    DataTablePagination,
  },
  mixins: [hasPermission],
  data() {
    return {
      search: '',
      pagination: {
        page: 1,
        pageCount: 1,
        itemsPerPage: 10,
      },
      headers: [
        { text: 'Account Number', value: 'account_number', sortable: false },
        { text: 'Name', value: 'name', sortable: false },
        { text: 'Phone', value: 'phone', sortable: false },
        { text: 'Email', value: 'email', sortable: false },
        { text: 'Deleted At', value: 'deleted_at', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      activeSearchFilter: 'name',
      searchFilters: [
        { text: 'Name', value: 'name' },
        { text: 'Account number', value: 'account_number' },
        { text: 'Phone number', value: 'phone' },
        { text: 'Email', value: 'email' },
      ],
      icons: {
        mdiAccountReactivate,
        mdiDeleteForever,
      },
      users: [],
      isUsersLoading: true,
      isUsersRefreshing: false,
      showConfirmRestoreDialog: false,
      isRestoreButtonLoading: false,
      showConfirmPermanentDeleteDialog: false,
      isPermanentDeleteButtonLoading: false,
      toActOn: null,
      toActOnName: null,
      breadcrumbs: [
        {
          text: 'Dashboard',
          disabled: false,
          to: { name: 'dashboard' },
        },
        {
          text: 'Former Customers',
          disabled: true,
          to: { name: 'former-customers' },
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['selectedStation']),
    restoreUserMessage() {
      return `Are you sure you want to restore ${this.toActOnName}?`
    },
    permanentDeleteUserMessage() {
      return `Are you sure you want to permanently delete ${this.toActOnName}? This action cannot be undone.`
    },
    refreshTable() {
      return `${this.selectedStation}|${this.search}|${this.activeSearchFilter}|${this.pagination.itemsPerPage}`
    },
  },
  watch: {
    refreshTable() {
      this.getUsers()
    },
    activeSearchFilter() {
      if (this.search !== '') {
        this.getUsers()
      }
    },
  },
  mounted() {
    this.getUsers()
  },
  methods: {
    showRestoreUserDialog(id, name) {
      this.toActOn = id
      this.toActOnName = name
      this.showConfirmRestoreDialog = true
    },
    showPermanentDeleteDialog(id, name) {
      this.toActOn = id
      this.toActOnName = name
      this.showConfirmPermanentDeleteDialog = true
    },
    getUsers: _.debounce(function () {
      this.isUsersLoading = true
      axios
        .get(`former-users?station_id=${this.selectedStation}&search=${this.search}&search_filter=${this.activeSearchFilter}&page=${this.pagination.page}&perPage=${this.pagination.itemsPerPage}`)
        .then(response => {
          this.users = response.data.data
          this.pagination.page = response.data.current_page
          this.pagination.pageCount = response.data.last_page
          this.isUsersLoading = false
          this.isUsersRefreshing = false
        })
        .catch(error => {
          this.isUsersLoading = false
          this.isUsersRefreshing = false
          this.$notification.error(error.response.data.message)
        })
    }, 500),
    refreshUsers() {
      this.isUsersRefreshing = true
      this.getUsers()
    },
    restoreUser() {
      this.isRestoreButtonLoading = true
      axios
        .put(`former-users/${this.toActOn}/restore`)
        .then(() => {
          this.$notification.success(`${this.toActOnName} restored successfully`)
          this.isRestoreButtonLoading = false
          this.showConfirmRestoreDialog = false
          this.getUsers()
        })
        .catch(error => {
          this.$notification.error(error.response.data.message)
          this.isRestoreButtonLoading = false
          this.showConfirmRestoreDialog = false
        })
    },
    permanentDeleteUser() {
      this.isPermanentDeleteButtonLoading = true
      axios
        .delete(`former-users/${this.toActOn}/permanent-delete`)
        .then(() => {
          this.$notification.success(`${this.toActOnName} permanently deleted`)
          this.isPermanentDeleteButtonLoading = false
          this.showConfirmPermanentDeleteDialog = false
          this.getUsers()
        })
        .catch(error => {
          this.$notification.error(error.response.data.message)
          this.isPermanentDeleteButtonLoading = false
          this.showConfirmPermanentDeleteDialog = false
        })
    },
    onPageChange(page) {
      this.pagination.page = page
      this.getUsers()
    },
    onItemsPerPageChange(itemsPerPage) {
      this.pagination.itemsPerPage = itemsPerPage
      this.getUsers()
    },
    onCustomerClick(customerId) {
      this.$router.push({ name: 'more-former-customer-details', params: { id: customerId } })
    },
    onSearchFilterChange(filter) {
      this.activeSearchFilter = filter
    },
  },
}
</script>
